import { AviationProxyUrl } from './../constants/urls';
import { Impacts } from './common/impacts';
import { DemoColors } from '../constants/colors';
import { initializeLayersPanel } from '../utils/layers';
import { DemoId, StrategyId, WeatherLayersId } from '../constants/sidebar';
import { defaultOverrides, radarButtonGL, alertsButtonGL } from './common/mapsGL';
import {
    radarGlobalButton,
    satelliteButton,
    fjetstreamButton,
    visibilityButton,
    fvisibilityButton
} from './common/buttons';
import { DataSources } from '../constants/dataSources';

const aviation = DataSources.aviation;
let activeAirline = 'DAL';

const airlines = {
    ASA: 'Alaska Airlines',
    AAL: 'American Airlines',
    DAL: 'Delta Airlines',
    FDX: 'FedEx',
    FFT: 'Frontier Airlines',
    JBU: 'JetBlue',
    SWA: 'Southwest Airlines',
    UAL: 'United Airlines',
    UPS: 'United Parcel Service (UPS)'
};

const getColor = (type, code = 'none') => {
    const upperCaseCode = code.toUpperCase();
    const lowerCaseCode = code.toLowerCase();
    let color: string;

    switch (type) {
        case 'flightrule': {
            color = DemoColors.aviation.flightrule[code];
            break;
        }
        case 'sigmet': {
            color = DemoColors.aviation.sigmet[upperCaseCode];
            break;
        }
        case 'status': {
            color = DemoColors.aviation.status[lowerCaseCode];
            break;
        }
        default: {
            color = '#999999';
        }
    }
    return color ?? '#999999';
};

const formattedPlace = (data) => {
    let str = data.city;

    if (data.state) {
        str += `, ${data.state}`;
    }

    if (data.countryFull) {
        str += `, ${data.countryFull}`;
    }
    return str;
};

let airportData = {};
let _aeris;
let lastCoord;

const rasterLayers = [{
    title: 'Conditions',
    buttons: [
        radarGlobalButton,
        visibilityButton
    ]
}, {
    title: 'Forecast',
    buttons: [
        fjetstreamButton,
        fvisibilityButton
    ]
}, {
    title: 'Severe',
    buttons: [
        alertsButtonGL,
        {
            title: 'Lightning Strikes',
            value: 'lightning-strikes-15m-icons'
        }, {
            id: 'stormcells',
            title: 'Storm Cells',
            value: 'stormcells'
        }]
}];

const mapsGLConfig = {
    layers: [{
        title: 'Conditions',
        buttons: [radarButtonGL,
            visibilityButton,
            {
                title: 'Winds',
                value: 'wind',
                options: {
                    ...defaultOverrides,
                    paint: {
                        sample: {
                            colorscale: {
                                normalized: true,
                                stops: [0, '#fff',
                                    1, '#fff'
                                ]
                            }
                        },
                        particle: {
                            size: 1,
                            count: Math.pow(128, 2)
                        }
                    }
                },
                segments: [{
                    title: 'Particles', value: 'wind-particles'
                }, { title: 'Barbs', value: 'wind-barbs' }]
            }]
    }, {
        title: 'Severe',
        buttons: [
            alertsButtonGL,
            {
                title: 'Lightning Strikes',
                value: 'lightning-strikes'
            },
            // {
            //     id: 'lightning',
            //     title: 'Lightning',
            //     segments: [{
            //         title: 'Lightning Strikes',
            //         value: 'lightning-strikes'
            //     },
            //     {
            //         title: 'Lightning Threats',
            //         value: 'lightning-threats'
            //     },
            //         {
            //             id: 'lightning-custom',
            //             title: 'Lightning: Custom',
            //             value: 'lightning-custom',
            //             options: {
            //                 ...lightningInfo,
            //                 ...lightningStyle,
            //                 legend: {
            //                     enabled: false
            //                 }
            //             },
            //         }
            //     ]
            // }, {
            {
                id: 'stormcells',
                title: 'Storm Cells',
                value: 'stormcells'
            }]
    }]
};

export const AviationConfig = ({ strategy, layers, weatherLayers }) => ({
    app: {
        map: {
            map: {
                zoomControl: false
            },
            refresh: 120,
            zoom: 4
        },
        mode: 'light',
        panels: {
            layers: {
                toggleable: true,
                buttons: [
                    {
                        title: 'Airports',
                        value: 'airports'
                    },
                    {
                        id: 'flights',
                        title: 'Flights',
                        filter: true,
                        segments: [{
                            title: 'Delta (DAL)',
                            value: 'DAL'
                        }, {
                            title: 'Alaska (ASA)',
                            value: 'ASA'
                        }, {
                            title: 'American (AAL)',
                            value: 'AAL'
                        }, {
                            title: 'FedEx (FDX)',
                            value: 'FDX'
                        }, {
                            title: 'Frontier (FFT)',
                            value: 'FFT'
                        }, {
                            title: 'JetBlue (JBU)',
                            value: 'JBU'
                        }, {
                            title: 'Southwest (SWA)',
                            value: 'SWA'
                        }, {
                            title: 'United (UAL)',
                            value: 'UAL'
                        }, {
                            title: 'UPS',
                            value: 'UPS'
                        }]
                    },
                    {
                        title: 'SIGMETs',
                        buttons: [{
                            id: 'sigmets',
                            value: 'sigmet',
                            title: 'US'
                        }, {
                            id: 'isigmets',
                            value: 'isigmet',
                            title: 'International'
                        }]
                    },
                    {
                        title: 'Satellite',
                        buttons: [
                            satelliteButton
                        ]
                    },
                    ...(weatherLayers === 'raster' ? rasterLayers : [])
                ]
            },
            timeline: {
                toggleable: true,
                range: {
                    value: [-4, 0],
                    range: {
                        min: -48,
                        max: 0
                    },
                    marksEvery: '% 12',
                    step: 2
                }
            },
            info: {
                sections: {
                    airport: {
                        renderer: (data, renderers) => {
                            if (!data || !data.airport || !data.airport?.place) return null;
                            return `
                                    <div class='airport'>
                                        <p class='name'>${data.airport?.place?.name} (${data.code})</p>
                                        <p class='loc'>${formattedPlace(data.airport.place)}</p>
                                        <div class='obs'>
                                            <h4>Latest Observation</h4>
                                            ${renderers.obs(data.ob, _aeris.utils)}
                                        </div>
                                    </div>
                                `;
                        }
                    }
                },
                views: {
                    flight: {
                        request: (data) => {
                            const departureCode = _aeris.utils.get(data, 'flight.origin.code');
                            const arrivalCode = _aeris.utils.get(data, 'flight.destination.code');
                            const request = _aeris.api();

                            request.addRequest(
                                _aeris.api().endpoint('places/airports').place(departureCode)
                            );
                            request.addRequest(
                                _aeris.api().endpoint('observations').place(departureCode)
                            );
                            request.addRequest(
                                _aeris.api().endpoint('places/airports').place(arrivalCode)
                            );
                            request.addRequest(
                                _aeris.api().endpoint('observations').place(arrivalCode)
                            );

                            return request;
                        },
                        views: [{
                            data: (data) => data.flight,
                            renderer: () => `
                                            <div class='flight-status'>
                                                <div class='awxjs__ui-row'>
                                                    <div class='awxjs__ui-expand'>Status</div>
                                                    <div class='awxjs__ui-expand value'> En-Route 
                                                    <div class='indicator' style='background:#999999;'></div></div>
                                                </div>
                                            </div>
                                        `
                        }, {
                            renderer: () => `
                                            <div class='airline'>
                                                <div class='awxjs__ui-row'>
                                                    <div class='awxjs__ui-expand'>Airline</div>
                                                    <div class='awxjs__ui-expand value'>${airlines[activeAirline]}</div>
                                                </div>
                                            </div>
                                        `
                        }, {
                            title: 'Departure',
                            renderer: 'airport',
                            data: (data) => {
                                if (Object.keys(data).length <= 1) return null;
                                const departureCode = data.flight.origin.code;
                                return {
                                    code: departureCode,
                                    airport: data[`places_airports_${departureCode}`],
                                    ob: data[`observations_${departureCode}`].ob
                                };
                            }
                        }, {
                            title: 'Arrival',
                            renderer: 'airport',
                            data: (data) => {
                                if (Object.keys(data).length <= 1) return null;
                                const arrivalCode = data.flight.destination.code;
                                return {
                                    code: arrivalCode,
                                    airport: data[`places_airports_${arrivalCode}`],
                                    ob: data[`observations_${arrivalCode}`].ob
                                };
                            }
                        }]
                    },
                    airport: {
                        request: (data) => {
                            const request = _aeris.api();
                            const forecastFields = 'timestamp,dateTimeISO,tempF,icon,weatherPrimary,windSpeedMPH,windSpeedMinMPH,windSpeedMaxMPH,windGustMPH,snowIN,precipIN'
                                .split(',')
                                .map((key) => `periods.${key}`);
                            request.addRequest(
                                _aeris
                                    .api()
                                    .endpoint('forecasts')
                                    .fields(forecastFields.join(','))
                                    .filter('3hr')
                                    .limit(7)
                            );
                            request.addRequest(_aeris.api().endpoint('threats').radius('50mi'));
                            request.addRequest(
                                _aeris
                                    .api()
                                    .endpoint('alerts')
                                    .fields('details.type,details.name,timestamps')
                            );
                            request.addRequest(_aeris.api().endpoint('phrases/summary'));
                            request.addRequest(
                                _aeris.api().endpoint('convective/outlook').action('contains')
                            );
                            request.addRequest(
                                _aeris
                                    .api()
                                    .endpoint('lightning/summary')
                                    .action('closest')
                                    .radius('60mi')
                                    .limit(100)
                            );

                            return request;
                        },
                        views: [{
                            data: (data) => {
                                if (data?.observations?.ob) return data.observations.ob;
                                return
                            },
                            renderer: (data) => {
                                if (!data) return null;
                                return `<div class='flightrule'>
                                 <div class='awxjs__ui-row'>
                                 <div class='awxjs__ui-expand'>Flight Rule</div>
                                 <div class='awxjs__ui-expand value'>${data.flightRule} 
                                 <div class='indicator' style='background:${getColor('flightrule', data.flightRule)};'>
                                 </div></div>
                                 </div>
                                 </div>`;
                            }
                        }, {
                            title: 'Active Threats',
                            renderer: 'threats'
                        }, {
                            title: 'Active Alerts',
                            renderer: 'alerts'
                        }, {
                            data: (data) => {
                                if (_aeris.utils.isEmpty(data)) return null;
                                return data;
                            },
                            renderer: (data) => {
                                if (!data) {
                                    return '';
                                }
                                return Impacts({ lastCoord });
                            }
                        }, {
                            title: 'Current Conditions',
                            renderer: 'obs'
                        }, {
                            title: 'Outlook',
                            renderer: 'outlook'
                        }, {
                            title: 'Short-Term Forecast',
                            renderer: 'forecast'
                        }]
                    },
                    sigmet: {
                        views: [{
                            data: (data) => data.sigmet,
                            renderer: (data) => (Object.keys(data).length === 0
                                ? null
                                : `<div class='sigmet'>${(
                                    data.rawAirSigmet || data.rawSigmet
                                ).replace(/\n/g, '<br>')}</div>`)
                        }]
                    },
                    localweather: {
                        request: (data) => {
                            const request = _aeris.api();
                            const forecastFields = 'timestamp,dateTimeISO,tempF,icon,weatherPrimary,windSpeedMPH,windSpeedMinMPH,windSpeedMaxMPH,windGustMPH,snowIN,precipIN, maxTempC, minTempC, maxTempF, minTempF'
                                .split(',')
                                .map((key) => `periods.${key}`);
                            request.addRequest(
                                _aeris.api().endpoint('forecasts').fields(forecastFields.join(',')).filter('1day')
                                    .limit(7)
                            );
                            request.addRequest(
                                _aeris.api().endpoint('threats').radius('50mi')
                            );
                            request.addRequest(
                                _aeris.api().endpoint('conditions')
                            );
                            request.addRequest(
                                _aeris.api().endpoint('alerts').fields('details.type,details.name,timestamps')
                            );
                            request.addRequest(_aeris.api().endpoint(
                                'phrases/summary'
                            ));
                            request.addRequest(
                                _aeris.api().endpoint('convective/outlook').action('contains')
                            );
                            request.addRequest(
                                _aeris.api().endpoint('lightning/summary').action('closest').radius('60mi')
                                    .limit(100)
                            );
                            return request;
                        },
                        views: [{
                            data: (data) => {
                                if (_aeris.utils.isEmpty(data)) return null;
                                return data;
                            },
                            renderer: (data) => {
                                if (!data) {
                                    return '';
                                }
                                return Impacts({ lastCoord });
                            }
                        }, {
                            title: 'Active Alerts',
                            renderer: 'alerts'
                        }, {
                            title: 'Active Threats',
                            renderer: 'threats'
                        }, {
                            title: 'Current Conditions',
                            renderer: 'obs'
                        }, {
                            title: 'Outlook',
                            renderer: 'outlook'
                        }]
                    }
                }
            }
        }
    },
    events: {
        ready: (aeris, app) => {
            _aeris = aeris;

            app.map.on('click', (e) => {
                lastCoord = e.data.coord;
                app.showInfoAtCoord(e.data.coord, 'localweather', 'Local Weather');
            });

            const ids = 'id:AYPY;id:BIKF;id:BKPR;id:CYEG;id:CYHZ;id:CYOW;id:CYQB;id:CYUL;id:CYVR;id:CYWG;id:CYXU;id:CYYC;id:CYYJ;id:CYYT;id:CYYZ;id:DAAG;id:DGAA;id:DNAA;id:DNMM;id:DTTA;id:EBBR;id:EBLG;id:EDDB;id:EDDC;id:EDDF;id:EDDG;id:EDDH;id:EDDK;id:EDDL;id:EDDM;id:EDDN;id:EDDP;id:EDDS;id:EDDT;id:EDDV;id:EDDW;id:EDLW;id:EDSB;id:EETN;id:EFHK;id:EGAA;id:EGAC;id:EGBB;id:EGCC;id:EGCN;id:EGFF;id:EGGD;id:EGGP;id:EGGW;id:EGHH;id:EGHI;id:EGKK;id:EGLC;id:EGLL;id:EGNM;id:EGNT;id:EGNX;id:EGPD;id:EGPF;id:EGPH;id:EGSH;id:EGSS;id:EGTE;id:EGUL;id:EGUN;id:EGVA;id:EGVN;id:EHAM;id:EHBK;id:EHEH;id:EICK;id:EIDW;id:EINN;id:EKBI;id:EKCH;id:EKYT;id:ELLX;id:ENBO;id:ENBR;id:ENGM;id:ENTC;id:ENVA;id:ENZV;id:EPGD;id:EPKK;id:EPKT;id:EPMO;id:EPPO;id:EPWA;id:EPWR;id:ESGG;id:ESMS;id:ESPA;id:ESSA;id:ETAR;id:EVRA;id:EYVI;id:FACT;id:FAGG;id:FAJS;id:FALE;id:GCLP;id:GCTS;id:GCXO;id:GMMN;id:GOOY;id:HAAB;id:HECA;id:HEGN;id:HKJK;id:HKMO;id:HLLT;id:HSSS;id:KABQ;id:KADW;id:KAFW;id:KAGS;id:KAMA;id:KATL;id:KAUS;id:KAVL;id:KBAB;id:KBAD;id:KBDL;id:KBFI;id:KBGR;id:KBHM;id:KBIL;id:KBLV;id:KBMI;id:KBNA;id:KBOI;id:KBOS;id:KBTR;id:KBUF;id:KBWI;id:KCAE;id:KCBM;id:KCHA;id:KCHS;id:KCID;id:KCLE;id:KCLT;id:KCMH;id:KCOF;id:KCOS;id:KCPR;id:KCRP;id:KCRW;id:KCVG;id:KCVS;id:KDAL;id:KDAY;id:KDBQ;id:KDCA;id:KDEN;id:KDFW;id:KDLF;id:KDLH;id:KDOV;id:KDSM;id:KDTW;id:KDYS;id:KEDW;id:KEND;id:KERI;id:KEWR;id:KFFO;id:KFLL;id:KFSM;id:KFTW;id:KFWA;id:KGEG;id:KGPT;id:KGRB;id:KGSB;id:KGSO;id:KGSP;id:KGUS;id:KHIB;id:KHMN;id:KHOU;id:KHSV;id:KHTS;id:KIAD;id:KIAH;id:KICT;id:KIND;id:KJAN;id:KJAX;id:KJFK;id:KJLN;id:KLAS;id:KLAX;id:KLBB;id:KLCK;id:KLEX;id:KLFI;id:KLFT;id:KLGA;id:KLIT;id:KLTS;id:KLUF;id:KMBS;id:KMCF;id:KMCI;id:KMCO;id:KMDW;id:KMEM;id:KMGE;id:KMGM;id:KMHT;id:KMIA;id:KMKE;id:KMLI;id:KMLU;id:KMOB;id:KMSN;id:KMSP;id:KMSY;id:KMUO;id:KMYR;id:KOAK;id:KOKC;id:KONT;id:KORD;id:KORF;id:KPAM;id:KPBI;id:KPDX;id:KPHF;id:KPHL;id:KPHX;id:KPIA;id:KPIT;id:KPWM;id:KRDU;id:KRFD;id:KRIC;id:KRND;id:KRNO;id:KROA;id:KROC;id:KRST;id:KRSW;id:KSAN;id:KSAT;id:KSAV;id:KSBN;id:KSDF;id:KSEA;id:KSFB;id:KSFO;id:KSGF;id:KSHV;id:KSJC;id:KSKA;id:KSLC;id:KSMF;id:KSNA;id:KSPI;id:KSPS;id:KSRQ;id:KSSC;id:KSTL;id:KSUS;id:KSUU;id:KSUX;id:KSYR;id:KSZL;id:KTCM;id:KTIK;id:KTLH;id:KTOL;id:KTPA;id:KTRI;id:KTUL;id:KTUS;id:KTYS;id:KVBG;id:KVPS;id:KWRB;id:LATI;id:LBBG;id:LBPD;id:LBSF;id:LBWN;id:LCLK;id:LCPH;id:LDZA;id:LEAL;id:LEBL;id:LEMD;id:LEMG;id:LEPA;id:LEST;id:LFBD;id:LFBO;id:LFBV;id:LFML;id:LFMN;id:LFOA;id:LFOT;id:LFPG;id:LFPO;id:LGAV;id:LGIR;id:LGTS;id:LHBP;id:LIBD;id:LICC;id:LICJ;id:LIEE;id:LIMC;id:LIME;id:LIMF;id:LIMJ;id:LIML;id:LIMZ;id:LIPE;id:LIPH;id:LIPX;id:LIPZ;id:LIRA;id:LIRF;id:LIRN;id:LIRP;id:LJLJ;id:LKPR;id:LLBG;id:LLOV;id:LMML;id:LOWW;id:LPFR;id:LPLA;id:LPPD;id:LPPR;id:LPPT;id:LQSA;id:LROP;id:LSGG;id:LSZH;id:LTAC;id:LTAF;id:LTAI;id:LTAJ;id:LTAZ;id:LTBA;id:LTBJ;id:LTBS;id:LTCE;id:LTCG;id:LTFC;id:LTFE;id:LTFJ;id:LXGB;id:LYBE;id:LYPG;id:LZIB;id:MDPC;id:MDSD;id:MKJP;id:MMAA;id:MMGL;id:MMHO;id:MMMX;id:MMMY;id:MMPR;id:MMSD;id:MMTJ;id:MMUN;id:MPTO;id:MUHA;id:MUVR;id:MWCR;id:MYNN;id:MZBZ;id:NCRG;id:NZAA;id:NZCH;id:NZWN;id:OBBI;id:OEDF;id:OEDR;id:OEJN;id:OEMA;id:OERK;id:OIIE;id:OIII;id:OIMM;id:OISS;id:OITT;id:OJAI;id:OKBK;id:OLBA;id:OMAA;id:OMDB;id:OMDW;id:OMSJ;id:OOMS;id:OPRN;id:OPST;id:ORBI;id:ORMM;id:OSAP;id:OSDI;id:OSLK;id:OTBD;id:PAFA;id:PANC;id:PGUM;id:PHNL;id:PR20;id:RCBS;id:RCKH;id:RCTP;id:RJAA;id:RJBB;id:RJCC;id:RJFF;id:RJFK;id:RJGG;id:RJNS;id:RJOO;id:RJTT;id:RJTY;id:RKJB;id:RKJK;id:RKPC;id:RKPK;id:RKSI;id:RKSO;id:RKSS;id:RKTU;id:ROAH;id:RODN;id:RPLC;id:RPLL;id:RPMD;id:RPVM;id:SAEZ;id:SBBE;id:SBBR;id:SBCF;id:SBCT;id:SBFL;id:SBGL;id:SBGR;id:SBSP;id:SBSV;id:SCEL;id:SELT;id:SEQM;id:SKBO;id:SPIM;id:SPZO;id:SUMU;id:TJSJ;id:TMJG;id:TNCM;id:UAAA;id:UACC;id:UAFM;id:UAKK;id:UBBB;id:UDYZ;id:UGTB;id:UKBB;id:UKCC;id:UKFF;id:UKHH;id:UKOO;id:ULLI;id:UMMS;id:UNKL;id:URSS;id:USSS;id:UTTT;id:UUBW;id:UUDD;id:UUEE;id:UUMU;id:UWUU;id:UWWW;id:VABB;id:VAGO;id:VCBI;id:VDPP;id:VDSR;id:VECC;id:VHHH;id:VIAR;id:VIDP;id:VISM;id:VMMC;id:VOBL;id:VOCI;id:VOCL;id:VOHS;id:VOMM;id:VOPN;id:VOTV;id:VRMM;id:VTBD;id:VTBS;id:VTCC;id:VVDN;id:VVNB;id:VVTS;id:VYMD;id:VYYY;id:WAAA;id:WADD;id:WARR;id:WBSB;id:WIII;id:WMKK;id:WSSS;id:YBBN;id:YMML;id:YPPH;id:YSCB;id:YSSY;id:ZBAA;id:ZBNY;id:ZBTJ;id:ZBYN;id:ZGGG;id:ZGHA;id:ZGKL;id:ZGNN;id:ZGSZ;id:ZHCC;id:ZHHH;id:ZJHK;id:ZJSY;id:ZLXY;id:ZPPP;id:ZSAM;id:ZSFZ;id:ZSHC;id:ZSJN;id:ZSNB;id:ZSNJ;id:ZSPD;id:ZSSS;id:ZSWZ;id:ZUCK;id:ZUGY;id:ZUUU;id:ZWWW;id:ZYHB;id:ZYTL;id:ZYTX';
            const airportsRequest = _aeris
                .api()
                .endpoint('observations')
                .action('search')
                .query(ids)
                .fields('id,loc,ob,place')
                .from('-2hours')
                .limit(1000);

            const airports = app.addSource('airports', 'vector', {
                refresh: 60,
                data: {
                    service: airportsRequest
                },
                style: {
                    marker: (data) => ({
                        svg: {
                            shape: {
                                type: 'rect',
                                fill: {
                                    color: getColor(
                                        'flightrule',
                                        _aeris.utils.get(data, 'ob.flightRule')
                                    )
                                },
                                stroke: {
                                    color: '#ffffff',
                                    width: 2
                                }
                            }
                        },
                        zIndex: 1,
                        size: [12, 12]
                    })
                }
            });
            airports.on('data:load', (e) => {
                if (e.data) {
                    airportData = (e.data.results || []).reduce((prev, current) => {
                        prev[current.id] = current;
                        return prev;
                    }, {});
                }
            });

            app.addSource('sigmets', 'geojson', {
                data: {
                    url: (params) => `${AviationProxyUrl}?do=sigmet`,
                    properties: {
                        id: 'icaoId',
                        category: 'hazard'
                    }
                },
                style: {
                    polygon: (item) => ({
                        fill: {
                            color: getColor('sigmet', item.hazard),
                            opacity: 0.4
                        },
                        stroke: {
                            color: getColor('sigmet', item.hazard),
                            width: 2
                        }
                    })
                }
            });
            app.addSource('isigmets', 'geojson', {
                data: {
                    url: () => `${AviationProxyUrl}?do=isigmet`,
                    properties: {
                        id: 'icaoId',
                        category: 'hazard'
                    }
                },
                style: {
                    polygon: (item) => ({
                        fill: {
                            color: getColor('sigmet', item.hazard),
                            opacity: 0.4
                        },
                        stroke: {
                            color: getColor('sigmet', item.hazard),
                            width: 2
                        }
                    })
                }
            });

            app.addSource('flights', 'vector', {
                data: {
                    url: (params) => {
                        activeAirline = params.filter || activeAirline;
                        return aviation.flights.sources[activeAirline].url;
                    },
                    coordinate: (data) => {
                        const { latitude, longitude } = data.last_position;
                        return { lat: latitude, lon: longitude };
                    }
                },
                style: {
                    marker: (data) => {
                        const angle = data.last_position.heading;
                        const shapeConfig = {
                            className: 'flights',
                            svg: {
                                shape: {
                                    type: 'path',
                                    fill: {
                                        color: '#cccccc'
                                    },
                                    stroke: {
                                        color: '#000000',
                                        width: 10
                                    },
                                    path: 'M497.25,357v-51l-204-127.5V38.25C293.25,17.85,275.4,0,255,0c-20.4,0-38.25,17.85-38.25,38.25V178.5L12.75,306v51 l204-63.75V433.5l-51,38.25V510L255,484.5l89.25,25.5v-38.25l-51-38.25V293.25L497.25,357z',
                                    transform: `rotate(${angle},255,255)`,
                                },
                                viewBox: '0 0 510 510'

                            },
                            zIndex: 2,
                            size: [24, 24]
                        };
                        const imageConfig = {
                            className: 'flights',

                            svg: {
                                image: {
                                    url: aviation.flights.icon.url,
                                    size: [36, 36],
                                    transform: `rotate(${angle})`,

                                }
                            },
                            zIndex: 2,
                            size: [36, 36]
                        }
                        return strategy === StrategyId.google ? shapeConfig : imageConfig;
                    }
                }
            });

            // when the map is zoomed or panned, the map is updated.
            // the sdk takes care of debouncing these updates
            function updateMap() {
                app.map.update();
            }
            app.map.on('zoom', updateMap);
            app.map.on('change:bounds', updateMap);

            app.map.on('marker:click', (e) => {
                if (e.data) {
                    lastCoord = { lat: e.data.data.lat, lon: e.data.data.long };
                    let data = e.data.data || {};
                    const source = data.awxjs_source;

                    if (source === 'airports') {
                        const { id, place = {} } = data;
                        const name = _aeris.utils.strings.toName(place.name);
                        data = { observations: airportData[id] };
                        app.showInfo('airport', `${id} - ${name}`).load({ p: id }, data);
                        return;
                    }

                    if (source === 'flights') {
                        data = { flight: data };
                        app.showInfo('flight', `Flight ${data.flight.ident}`).load(null, data);
                    }
                }
            });
            app.map.on('shape:click', (e) => {
                if (e.data) {
                    lastCoord = { lat: e.data.data.lat, lon: e.data.data.long };
                    const data = e.data.data || {};
                    const source = data.awxjs_source;

                    if (source === 'sigmets') {
                        app.showInfo('sigmet',
                            `${data.icaoId} - ${_aeris.utils.strings.toName(data.hazard)} SIGMET`,
                            { sigmet: data }
                        );
                    }
                }
            });
            aeris.modules().then((modules) => {
                if (StrategyId.leaflet === strategy) {
                    app.modules.insertAt(2, modules.aviation.StationPlots);
                }

                setTimeout(() => {
                    initializeLayersPanel(
                        aeris,
                        app,
                        {
                            demo: DemoId.aviation,
                            weatherLayers
                        },
                        {
                            mapsGLConfig,
                            layersFromParams: layers
                        }
                    ).then(({ controller }) => {
                        app.on('layer:deselect', (e) => {
                            const { id } = e.data;

                            if (id === 'stormcells' && weatherLayers === WeatherLayersId.mapsGL) {
                                controller.removeWeatherLayer('stormcells');
                            }
                        });
                    });
                }, 250);
            });
        }
    }
});
